import type { TFunction } from 'i18next'

/**
 * The single place a domain enum value (status, competition role, platform
 * role, team role) turns into display text. Every screen that shows one of
 * these values must call the matching function here rather than
 * interpolating the raw API string or hand-rolling `.replace(/_/g, ' ')` —
 * that divergence is what let the same status render correctly in one badge
 * and as raw English two lines below it.
 *
 * `defaultValue` falls back to the raw value itself so an enum member added
 * on the backend without a matching translation key degrades to readable
 * (if untranslated) text instead of a missing-key warning string.
 */

export function competitionStatusLabel(t: TFunction, status: string): string {
  return t(`common:status.competition.${status}`, { defaultValue: status })
}

export function formVersionStatusLabel(t: TFunction, status: string): string {
  return t(`common:status.formVersion.${status}`, { defaultValue: status })
}

export function resultRunStatusLabel(t: TFunction, status: string): string {
  return t(`common:status.resultRun.${status}`, { defaultValue: status })
}

export function submissionStatusLabel(t: TFunction, status: string): string {
  return t(`common:status.submission.${status}`, { defaultValue: status })
}

/**
 * Session 7 (item 4, UX-279/UX-294): the Participant-facing submission
 * status label. `common:status.submission.ready` ("已準備") is shared with
 * the Organizer/Admin submission list Badge and Global Search results
 * (`competition-submissions-page.tsx`, `competition-global-search.tsx`),
 * where the upstream spec's exact canonical Participant copy — the longer
 * "已準備，尚未正式送出" — would be an out-of-place run-on sentence in a
 * table cell/search-result badge that was never asked to change. Rather
 * than lengthen the shared key globally, this checks a Participant-only
 * override namespace first, for `ready` only, and falls back to the exact
 * same shared label as every other status and every non-Participant caller.
 */
export function participantSubmissionStatusLabel(t: TFunction, status: string): string {
  if (status === 'ready') {
    return t('student:submission.status.readyNotSubmitted')
  }
  return submissionStatusLabel(t, status)
}

export function fileStatusLabel(t: TFunction, status: string): string {
  return t(`common:status.file.${status}`, { defaultValue: status })
}

/** A user's role within one competition: `competition_admin`, `judge`, `student`, ... */
export function competitionRoleLabel(t: TFunction, role: string): string {
  return t(`common:status.competitionRole.${role}`, { defaultValue: role })
}

/** The platform-wide role column on `users`: `user` or `super_admin`. */
export function platformRoleLabel(t: TFunction, role: string): string {
  return t(`common:status.platformRole.${role}`, { defaultValue: role })
}

/** A member's role within one team: `leader`, `member`, `teacher`. */
export function teamRoleLabel(t: TFunction, role: string): string {
  return t(`common:status.teamRole.${role}`, { defaultValue: role })
}

/** `users.status`: `active`, `suspended`, `deleted`. */
export function userStatusLabel(t: TFunction, status: string): string {
  return t(`common:status.user.${status}`, { defaultValue: status })
}

/** UX-016/017: `draft`, `submitted`, `under_review`, `changes_requested`, `approved`, `rejected`, `cancelled`. */
export function competitionRequestStatusLabel(t: TFunction, status: string): string {
  return t(`common:status.competitionRequest.${status}`, { defaultValue: status })
}
